import { Routes } from "@angular/router";
import { AuthGuard } from "@app/shared/auth/guards/auth.guard";
import { IAuthGuardOptions } from "@app/shared/auth/interfaces/auth.interface";
import { EAccessType } from "@app/shared/auth/enums/auth.enum";
import { ESessionStatus } from "@app/shared/general/sessions/enums/session.enums";
import { initiate2FaRecovery } from "./resolvers/initiate-2fa-recovery.resolver";
import { CanDeactivateMaintenanceBreak } from "./guards/can-deactivate-maintenance-break.guard";

export const authRoutes: Routes = [
  {
    path: "login",
    loadComponent: () => import("./pages/login/login.component").then(c => c.LoginComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        preventTokenHolders: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "change-temp-password",
    loadComponent: () => import("./pages/change-temp-password/change-temp-password.component").then(c => c.ChangeTempPasswordComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.ChangeTempPassword,
        allowAccessWithoutSession: true,
        preventLoggedIn: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "change-expired-password",
    loadComponent: () => import("./pages/change-expired-password/change-expired-password.component").then(c => c.ChangeExpiredPasswordComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.ChangeExpiredPassword,
        allowAccessWithoutSession: true,
        preventLoggedIn: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "reset-password",
    loadComponent: () => import("./pages/reset-password/reset-password.component").then(c => c.ResetPasswordComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.ResetPassword,
        allowAccessWithoutSession: true,
        preventLoggedIn: true,
        redirectTo: "/auth/forget-password",
      } as IAuthGuardOptions,
    },
  },
  {
    path: "app-download",
    loadComponent: () => import("./pages/app-download/app-download.component").then(c => c.AppDownloadComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.ObtainAccess,
        sessionStatus: ESessionStatus.PendingVerification,
        allowNonObtainedAccess: true,
        preventAuthenticated: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "two-factor",
    loadComponent: () => import("./pages/two-factor/two-factor.component").then(c => c.TwoFactorComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.ObtainAccess,
        sessionStatus: [ESessionStatus.PendingVerification, ESessionStatus.Authenticated, ESessionStatus.Denied],
        preventAuthenticated: true,
        allowNonObtainedAccess: true,
        allowExpiredSession: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "banned-account",
    loadComponent: () => import("./pages/banned-account/banned-account.component").then(c => c.BannedAccountComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        preventTokenHolders: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "forget-password",
    loadComponent: () => import("./pages/forget-password/forget-password.component").then(c => c.ForgetPasswordComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        preventTokenHolders: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "contact-admin",
    loadComponent: () => import("./pages/contact-admin/contact-admin.component").then(c => c.ContactAdminComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        preventTokenHolders: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "verify-reset-password",
    loadComponent: () => import("./pages/verify-reset-password/verify-reset-password.component").then(c => c.VerifyResetPasswordComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.VerifyResetPassword,
        allowAccessWithoutSession: true,
        redirectTo: "/auth/forget-password",
      } as IAuthGuardOptions,
    },
  },
  {
    path: "verify-2fa-recovery",
    loadComponent: () => import("./pages/verify-2fa-recovery/verify-2fa-recovery.component").then(c => c.Verify2FaRecoveryComponent),
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        accessType: EAccessType.VerifyLoginByRecoveryOtp,
        sessionStatus: ESessionStatus.PendingVerification,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "account-recovery",
    loadComponent: () => import("./pages/initiate-2fa-recovery/initiate-2fa-recovery.component").then(c => c.Initiate2FaRecoveryComponent),
    canActivate: [AuthGuard],
    resolve: { initiate2FaRecovery },
    data: {
      authGuardOptions: {
        accessType: [EAccessType.VerifyLoginByRecoveryOtp, EAccessType.ObtainAccess],
        sessionStatus: ESessionStatus.PendingVerification,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "maintenance-break",
    loadComponent: () => import("./pages/maintenance-break/maintenance-break.component").then(c => c.MaintenanceBreakComponent),
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateMaintenanceBreak],
    data: {
      authGuardOptions: {
        redirectTo: "/",
        allowAnyAccessType: true,
        allowAccessWithoutSession: true,
      } as IAuthGuardOptions,
    },
  },
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
];
